import React, { useEffect, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';

interface FilterConfig {
  name: string;
  label: string;
  type: 'text' | 'date' | 'select' | 'number' | 'multiselect';
  options?: { value: string; label: string }[];
  width?: string;
}

const MULTI_SEPARATOR = '|';

const splitMultiValue = (value: string | undefined) =>
  (value || '').split(MULTI_SEPARATOR).map((part) => part.trim()).filter(Boolean);

const MultiSelectFilter: React.FC<{
  name: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (name: string, value: string) => void;
}> = ({ name, options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = splitMultiValue(value);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const toggle = (optionValue: string) => {
    const next = new Set(selected);
    if (next.has(optionValue)) next.delete(optionValue);
    else next.add(optionValue);
    onChange(name, Array.from(next).join(MULTI_SEPARATOR));
  };

  const summary = selected.length === 0
    ? 'All'
    : options
        .filter((option) => selected.includes(option.value))
        .map((option) => option.label)
        .join(', ') || selected.join(', ');

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center justify-between gap-2 rounded border bg-white p-2 text-left text-sm text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="truncate">{summary}</span>
        <span className="text-xs text-slate-500">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 max-h-48 w-full overflow-auto rounded border border-slate-200 bg-white p-1 shadow-lg">
          {options.length === 0 ? (
            <div className="px-2 py-1 text-sm text-slate-500">No roles</div>
          ) : (
            options.map((option) => (
              <label
                key={option.value}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-slate-800 hover:bg-slate-50"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(option.value)}
                  onChange={() => toggle(option.value)}
                />
                <span>{option.label}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
};

interface TableFilterProps<T> {
  filters: FilterConfig[];
  onApply: (filters: Record<string, string>) => void;
  onClear: () => void;
  onToggle?: () => void;
  visible?: boolean;
  className?: string;
}

const TableFilter = <T extends object>({
  filters,
  onApply,
  onClear,
  onToggle,
  visible = false,
  className = ''
}: TableFilterProps<T>) => {
  const [filterValues, setFilterValues] = useState<Record<string, string>>(
    filters.reduce((acc, filter) => ({ ...acc, [filter.name]: '' }), {})
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilterValues(prev => ({ ...prev, [name]: value }));
  };

  const setNamedValue = (name: string, value: string) => {
    setFilterValues(prev => ({ ...prev, [name]: value }));
  };

  const handleApply = () => {
    onApply(filterValues);
  };

  const handleClear = () => {
    const clearedValues = filters.reduce((acc, filter) => ({ ...acc, [filter.name]: '' }), {});
    setFilterValues(clearedValues);
    onClear();
  };

  if (!visible) return null;

  return (
    <div className={`w-full  bg-gray-50 p-2 rounded-b-sm border ${className}`}>
      <div className="flex justify-between items-end">
        {/* Filter fields container */}
        <div className="flex flex-wrap gap-4">
          {filters.map(filter => (
            <div 
              key={filter.name} 
              className={`flex flex-col ${filter.width || 'min-w-[200px]'}`}
            >
              <label className="text-sm font-medium text-gray-700 mb-1">
                {filter.label}
              </label>
              {filter.type === 'multiselect' ? (
                <MultiSelectFilter
                  name={filter.name}
                  options={filter.options || []}
                  value={filterValues[filter.name] || ''}
                  onChange={setNamedValue}
                />
              ) : filter.type === 'select' ? (
                <select
                  name={filter.name}
                  value={filterValues[filter.name] || ''}
                  onChange={handleChange}
                  className="p-2 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All</option>
                  {filter.options?.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type={filter.type}
                  name={filter.name}
                  value={filterValues[filter.name] || ''}
                  onChange={handleChange}
                  className="p-2 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              )}
            </div>
          ))}
        </div>

        {/* Buttons container */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleClear}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 text-sm transition-colors"
          >
            Clear
          </button>
          <button
            onClick={handleApply}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm transition-colors"
          >
            Apply
          </button>
          {onToggle && (
            <button
              onClick={onToggle}
              className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
              title="Close filter"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TableFilter;